import React, { useEffect, useState } from "react";
import { X, MapPin, Package, CreditCard, Truck, StickyNote, FileText, Copy, ExternalLink, Trash2, Ban, ShoppingBag, Calendar } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { OfflineReceiptForm } from "./OfflineReceiptForm";
import { isOfflinePayment, DELIVERY_LABELS } from "../OfflinePaymentOptions";

/**
 * OrderDetailModal — click an order row in Admin → Orders and this
 * pops open with the complete picture: fulfillment type, pickup notes,
 * shipping address, line items, P&L breakdown, payment gateway ID,
 * status actions. Removes the need to open the PDF just to read the
 * customer's pickup instructions or spot-check what they bought.
 */
const OrderDetailModal = ({ order, pl, onClose, onSetStatus, onDelete, onRefresh }) => {
    const [slots, setSlots] = useState([]);
    const [assigning, setAssigning] = useState(false);
    const [selectedSlotId, setSelectedSlotId] = useState(order?.assigned_slot_id || "");

    const isPickup = order?.fulfillment_type === "in_person_pickup";

    useEffect(() => {
        // Only fetch slot options for pickup orders — no need to bother the
        // API when we're looking at a ship order.
        if (!isPickup) return;
        api.get("/admin/pickup-slots").then((r) => setSlots(Array.isArray(r.data) ? r.data : [])).catch(() => {});
        setSelectedSlotId(order?.assigned_slot_id || "");
    }, [order?.id, isPickup]);

    if (!order) return null;

    const method = (order.payment_method || "").toLowerCase();
    const ref = order.payment_reference || "";
    const stripeUrl = method === "stripe" && ref.startsWith("pi_")
        ? `https://dashboard.stripe.com/payments/${ref}`
        : method === "paypal"
            ? `https://www.paypal.com/activity/payment/${ref}`
            : null;

    const copyRef = async () => {
        try { await navigator.clipboard.writeText(ref); toast.success("Payment ID copied"); }
        catch { toast.error("Copy failed"); }
    };

    const assignSlot = async (slotId) => {
        setAssigning(true);
        try {
            await api.post(`/admin/orders/${order.id}/assign-slot`, { slot_id: slotId || null });
            toast.success(slotId ? "Pickup slot assigned — customer emailed" : "Pickup slot cleared");
            setSelectedSlotId(slotId || "");
            if (onRefresh) onRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to assign slot");
        } finally {
            setAssigning(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[80] bg-black/50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto"
            onClick={onClose}
            data-testid="admin-order-detail-modal"
        >
            <div
                className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl my-4 relative"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-4 p-5 border-b border-black/5 sticky top-0 bg-white rounded-t-2xl z-10">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-mono text-xs uppercase tracking-wider text-[#4A5568]">{order.order_number}</p>
                            <StatusPill label={order.payment_status} kind="payment" />
                            <StatusPill label={order.fulfillment_status} kind="fulfillment" />
                            {isPickup && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800">
                                    <MapPin className="w-3 h-3" /> Pickup
                                </span>
                            )}
                        </div>
                        <p className="font-heading text-2xl text-[#1C252E] font-medium mt-1 truncate">{order.customer_name || "—"}</p>
                        <p className="text-xs text-[#4A5568] truncate">
                            {order.customer_email || "no email"} · {order.customer_phone || "no phone"}
                            {order.created_at && <> · {new Date(order.created_at).toLocaleString()}</>}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="admin-order-detail-close"
                        className="text-slate-400 hover:text-slate-700 shrink-0"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-5">
                    {/* Pickup notes / shipping address — highest priority so admin sees it first */}
                    {isPickup ? (
                        <Section icon={<MapPin className="w-4 h-4" />} title="In-person pickup" accent="emerald">
                            {order.notes ? (
                                <div>
                                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1">Customer&apos;s pickup instructions</p>
                                    <p className="text-sm text-[#1C252E] whitespace-pre-wrap bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                                        {order.notes}
                                    </p>
                                </div>
                            ) : (
                                <p className="text-sm text-[#4A5568] italic">No pickup notes provided by customer.</p>
                            )}
                            {/* Slot assignment — admin designates when the customer can pick up. */}
                            <div className="mt-4 pt-4 border-t border-emerald-200/60">
                                <p className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-2 flex items-center gap-1.5">
                                    <Calendar className="w-3 h-3" /> Assign pickup window
                                </p>
                                {slots.length === 0 ? (
                                    <p className="text-xs text-[#4A5568] italic">No pickup slots published yet — go to Admin → Pickup slots to add one.</p>
                                ) : (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <select
                                            value={selectedSlotId}
                                            onChange={(e) => assignSlot(e.target.value)}
                                            disabled={assigning}
                                            data-testid="order-assign-slot"
                                            className="flex-1 min-w-[220px] px-3 py-2 text-sm border border-black/15 rounded-lg bg-white focus:outline-none focus:border-[#E53935] disabled:opacity-50"
                                        >
                                            <option value="">— no window assigned —</option>
                                            {slots.filter((s) => s.active || s.id === selectedSlotId).map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.display}{s.location ? ` · ${s.location}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                        {order.assigned_slot_snapshot && (
                                            <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                                                Emailed to customer
                                            </span>
                                        )}
                                    </div>
                                )}
                                {order.assigned_slot_snapshot?.display && (
                                    <p className="text-xs text-[#4A5568] mt-2">
                                        Current: <strong className="text-[#1C252E]">{order.assigned_slot_snapshot.display}</strong>
                                        {order.assigned_slot_snapshot.location ? ` · ${order.assigned_slot_snapshot.location}` : ""}
                                    </p>
                                )}
                            </div>
                        </Section>
                    ) : (
                        <Section icon={<Truck className="w-4 h-4" />} title="Ship to">
                            <p className="text-sm text-[#1C252E]">
                                {[order.shipping_address, order.shipping_city, order.shipping_state, order.shipping_zip].filter(Boolean).join(", ") || "No address on file"}
                            </p>
                            {order.notes && (
                                <div className="mt-2 pt-2 border-t border-black/5">
                                    <p className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] mb-1 flex items-center gap-1"><StickyNote className="w-3 h-3" />Customer notes</p>
                                    <p className="text-sm text-[#1C252E] whitespace-pre-wrap">{order.notes}</p>
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Line items */}
                    {isPickup && <Section icon={<Truck className="w-4 h-4" />} title="Customer address"><p data-testid="order-customer-address" className="text-sm text-[#1C252E] break-words">{[order.shipping_address, order.shipping_city, order.shipping_state, order.shipping_zip].filter(Boolean).join(", ") || "No address on file"}</p></Section>}
                    <Section icon={<Package className="w-4 h-4" />} title={`Items (${order.items?.length || 0})`}>
                        <div className="space-y-2">
                            {(order.items || []).map((it, i) => (
                                <div key={i} className="flex items-center gap-3 text-sm">
                                    {it.image_url ? (
                                        <img src={it.image_url} alt="" className="w-12 h-12 object-cover rounded-lg bg-[#F4F1EA]" />
                                    ) : (
                                        <div className="w-12 h-12 rounded-lg bg-[#F4F1EA] flex items-center justify-center">
                                            <ShoppingBag className="w-4 h-4 text-[#4A5568]/40" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <p className="font-medium text-[#1C252E] truncate">{it.name || "—"}</p>
                                        <p className="text-xs text-[#4A5568]">
                                            Qty {it.quantity} × ${Number(it.price || 0).toFixed(2)}
                                            {it.cost_at_sale != null && <> · cost ${Number(it.cost_at_sale).toFixed(2)}</>}
                                        </p>
                                    </div>
                                    <p className="font-mono text-sm text-[#1C252E]">${(Number(it.price || 0) * Number(it.quantity || 0)).toFixed(2)}</p>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Totals + P&L */}
                    <Section icon={<CreditCard className="w-4 h-4" />} title="Totals & margin">
                        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
                            <Row label="Subtotal" value={`$${Number(order.subtotal || 0).toFixed(2)}`} />
                            <Row label="Shipping" value={`$${Number(order.shipping || 0).toFixed(2)}`} />
                            {order.discount ? <Row label="Discount" value={`-$${Number(order.discount).toFixed(2)}`} accent="green" /> : null}
                            {order.roundup_amount ? <Row label="Round-up donation" value={`$${Number(order.roundup_amount).toFixed(2)}`} accent="red" /> : null}
                            <Row label="Total" value={`$${Number(order.total || 0).toFixed(2)}`} bold />
                            {pl && (
                                <>
                                    <Row label="Cost recovered" value={`$${Number(pl.cogs || 0).toFixed(2)}`} accent="amber" />
                                    <Row label="→ General Support Fund" value={`$${Number(pl.net_to_gsf || 0).toFixed(2)}`} bold accent="red" />
                                </>
                            )}
                        </div>
                    </Section>

                    {/* Payment gateway */}
                    <Section icon={<CreditCard className="w-4 h-4" />} title="Payment">
                        {isOfflinePayment(method) && order.payment_status === "pending" && order.fulfillment_status !== "cancelled" && <div className="mb-4"><OfflineReceiptForm key={order.id} item={{ id: order.id, kind: "order", method, name: order.customer_name, amount: order.total, delivery: order.payment_delivery || (method === "cod" ? "pickup" : null), can_confirm: true }} onConfirmed={() => onRefresh?.()} /></div>}
                        {(order.payment_delivery || order.payment_received_via) && <p data-testid="order-offline-delivery" className="mb-2 text-xs text-[#4A5568]">{order.payment_status === "paid" ? "Received" : "Expected"}: {DELIVERY_LABELS[order.payment_received_via || order.payment_delivery]}</p>}
                        <div className="flex items-center gap-2 flex-wrap text-sm">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider text-[10px] ${method === "stripe" ? "bg-indigo-100 text-indigo-700" : method === "paypal" ? "bg-blue-100 text-blue-700" : method === "cod" ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
                                {method === "cod" ? "Cash on pickup" : (method || "unpaid")}
                            </span>
                            {ref ? (
                                <>
                                    <code className="font-mono text-xs text-[#1C252E] bg-[#F4F1EA] px-2 py-0.5 rounded" title={ref}>{ref}</code>
                                    <button onClick={copyRef} className="text-slate-400 hover:text-[#E53935]" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
                                    {stripeUrl && (
                                        <a href={stripeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#635BFF] hover:underline">
                                            Open in {method === "stripe" ? "Stripe" : "PayPal"} <ExternalLink className="w-3 h-3" />
                                        </a>
                                    )}
                                </>
                            ) : (
                                <span className="text-xs text-slate-500 italic">Payment not yet confirmed</span>
                            )}
                            {order.paid_at && <span className="text-xs text-[#4A5568] ml-auto">Paid {new Date(order.paid_at).toLocaleString()}</span>}
                        </div>
                    </Section>
                </div>

                {/* Footer actions */}
                <div className="p-5 border-t border-black/5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                        <label className="text-xs uppercase tracking-wider font-bold text-[#4A5568]">Status</label>
                        <select
                            value={order.fulfillment_status}
                            onChange={(e) => onSetStatus(order.id, e.target.value)}
                            data-testid="admin-order-detail-status"
                            className="rounded-full border border-black/10 px-3 py-1.5 text-xs bg-white"
                        >
                            <option value="new">New</option>
                            <option value="processing">Processing</option>
                            <option value="shipped">Shipped</option>
                            <option value="delivered">Delivered</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <a
                            href={`${API}/books/invoice/${order.id}.pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-medium text-[#E53935] hover:underline"
                        >
                            <FileText className="w-3.5 h-3.5" /> Invoice PDF
                        </a>
                        {order.fulfillment_status !== "cancelled" && (
                            <button
                                onClick={() => onSetStatus(order.id, "cancelled")}
                                data-testid="admin-order-detail-cancel"
                                className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-900"
                            >
                                <Ban className="w-3.5 h-3.5" /> Cancel
                            </button>
                        )}
                        {order.fulfillment_status === "cancelled" && (
                            <button
                                onClick={() => onDelete(order.id, order.order_number, onClose)}
                                data-testid="admin-order-detail-delete"
                                className="inline-flex items-center gap-1 text-xs font-medium text-red-700 hover:text-red-900"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete permanently
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const Section = ({ icon, title, accent, children }) => (
    <div className={`rounded-xl border p-4 ${accent === "emerald" ? "bg-emerald-50/40 border-emerald-200" : "bg-white border-black/5"}`}>
        <p className="text-[11px] uppercase tracking-wider font-bold text-[#4A5568] flex items-center gap-1.5 mb-2">
            {icon} {title}
        </p>
        {children}
    </div>
);

const Row = ({ label, value, bold, accent }) => {
    const color = accent === "red" ? "text-[#E53935]" : accent === "amber" ? "text-amber-700" : accent === "green" ? "text-green-700" : "text-[#1C252E]";
    return (
        <>
            <span className="text-[#4A5568]">{label}</span>
            <span className={`text-right font-mono ${bold ? "font-bold" : ""} ${color}`}>{value}</span>
        </>
    );
};

const StatusPill = ({ label, kind }) => {
    if (!label) return null;
    const paymentStyles = { paid: "bg-green-100 text-green-800", pending: "bg-amber-100 text-amber-800", failed: "bg-red-100 text-red-800", refunded: "bg-slate-200 text-slate-700" };
    const fulfillmentStyles = { new: "bg-blue-100 text-blue-800", processing: "bg-indigo-100 text-indigo-800", shipped: "bg-purple-100 text-purple-800", delivered: "bg-green-100 text-green-800", cancelled: "bg-slate-200 text-slate-600" };
    const styles = kind === "payment" ? paymentStyles : fulfillmentStyles;
    return (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${styles[label] || "bg-slate-100 text-slate-700"}`}>
            {label}
        </span>
    );
};

export default OrderDetailModal;
